import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity } from 'lucide-react';

interface AdminLoadingStateProps {
  /**
   * Component title (e.g., "System Health", "Audit Logs")
   */
  title?: string;

  /**
   * Description text to show while loading
   */
  description?: string;

  /**
   * Number of skeleton rows to display
   * @default 3
   */
  skeletonCount?: number;
}

/**
 * AdminLoadingState - Consistent loading UI for admin components
 *
 * Provides a standardized loading display with:
 * - Animated skeleton screens
 * - Clear loading message
 * - Consistent styling
 *
 * @example
 * ```tsx
 * <AdminLoadingState
 *   title="System Health"
 *   description="Loading system health metrics..."
 *   skeletonCount={5}
 * />
 * ```
 */
export function AdminLoadingState({
  title = 'Admin Component',
  description = 'Loading data...',
  skeletonCount = 3,
}: AdminLoadingStateProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 animate-pulse" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
            <div className="h-8 bg-muted animate-pulse rounded" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
